import { useMutation, useQuery } from "@tanstack/react-query";

const baseUrl = import.meta.env.VITE_BACKEND_URL || "";

// Types
interface SendTestEmailParams {
  to: string;
  subject: string;
  content: string;
  template: "daily_brief" | "weekly_digest" | "welcome" | "custom";
}

interface SendCampaignParams {
  subject: string;
  content: string;
  template: string;
  segment: "all" | "pro" | "free";
  scheduleAt?: string;
}

interface SaveDraftParams {
  id?: string;
  subject: string;
  content: string;
  template: string;
  segment: "all" | "pro" | "free";
}

interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  content: string;
}

interface SegmentCounts {
  all: number;
  pro: number;
  free: number;
}

// Generate article from tweet
interface GenerateArticleParams {
  tweetUrl: string;
  style: "informative" | "conversational" | "analytical";
  length: "short" | "medium" | "long";
  includeAnalysis?: boolean;
  includeQuotes?: boolean;
}

export interface GenerateArticleResponse {
  article: {
    title: string;
    content: string;
    wordCount: number;
  };
}

// Send test email
export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (params: SendTestEmailParams) => {
      const response = await fetch(`${baseUrl}/api/admin/email/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error("Failed to send test email");
      }
      return response.json();
    },
  });
}

// Send campaign
export function useSendCampaign() {
  return useMutation({
    mutationFn: async (params: SendCampaignParams) => {
      const response = await fetch(`${baseUrl}/api/admin/email/campaign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error("Failed to send campaign");
      }
      const data = await response.json();
      return data.data as { recipientCount?: number };
    },
  });
}

// Save draft
export function useSaveDraft() {
  return useMutation({
    mutationFn: async (params: SaveDraftParams) => {
      const response = await fetch(`${baseUrl}/api/admin/email/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error("Failed to save draft");
      }
      const data = await response.json();
      return data.data as { id: string };
    },
  });
}

// Fetch email templates
export function useEmailTemplates() {
  return useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/admin/email/templates`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch templates");
      }
      const data = await response.json();
      return data.data as EmailTemplate[];
    },
  });
}

// Fetch segment counts
export function useSegmentCounts() {
  return useQuery({
    queryKey: ["segment-counts"],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/admin/email/segments`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch segment counts");
      }
      const data = await response.json();
      return data.data as SegmentCounts;
    },
  });
}

// Generate article from tweet
export function useGenerateArticle() {
  return useMutation({
    mutationFn: async (params: GenerateArticleParams): Promise<GenerateArticleResponse> => {
      const response = await fetch(`${baseUrl}/api/admin/email/generate-article`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error("Failed to generate article");
      }
      const data = await response.json();
      return data.data as GenerateArticleResponse;
    },
  });
}
